import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Autocomplete, TAutocompleteOption } from '@qapline/ui';
import { debounce, isEqual } from 'lodash';

import { useLazyGetGeocodedAddressQuery } from '../../api';
import { TGeocodedAddress } from '../../types';
import { formatGeocodedAddress } from '../../utils';

export interface ILocationSearchProps {
    onSelect?: (value: TGeocodedAddress | null) => void;
    selected?: TGeocodedAddress | null;
}

export const LocationSearch = ({ onSelect, selected }: ILocationSearchProps) => {
    const { t } = useTranslation('common');
    const [getGeocodedAddress, { data }] = useLazyGetGeocodedAddressQuery();
    const availableData = useMemo(() => {
        const addresses = data ?? [];
        if (!selected || addresses.some((item) => isEqual(item, selected))) return addresses;
        return [...addresses, selected];
    }, [data, selected]);
    const options = useMemo((): TAutocompleteOption[] => {
        return availableData.map((item, index) => {
            return {
                label: formatGeocodedAddress(item),
                value: index.toString(),
            };
        });
    }, [availableData]);

    const handleTyping = useCallback(
        debounce((value: string) => {
            if (!value) return;
            getGeocodedAddress(value);
        }, 300),
        [getGeocodedAddress],
    );

    const handleSelect = (selectedValue: string | null) => {
        if (selectedValue === null) {
            onSelect?.(null);
            return;
        }
        const selectedOption = availableData[Number(selectedValue)];
        if (!selectedOption) return;
        onSelect?.(selectedOption);
    };

    return (
        <Autocomplete
            filterValues={false}
            options={options}
            onInputChange={handleTyping}
            onValueChange={handleSelect}
            placeholder={t('components.locationSearch.placeholder')}
            selected={selected ? availableData.findIndex((item) => isEqual(item, selected)).toString() : selected}
        />
    );
};
